import { z } from "zod";
import { captureError } from '../../../utils/sentryHelpers';

// Speaker Schema - matches the lecture speaker schema
const speakerSchema = z.object({
  id: z.string(),
  speakerType: z.enum(['employee', 'user']).optional(),
  employeeId: z.string().optional(),
  userId: z.string().optional(),
  firstName: z.string(),
  lastName: z.string(),
  middleName: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  department: z.string().optional().nullable(),
  image: z.object({
    fileName: z.string(),
    alt: z.string().optional().nullable()
  }).optional().nullable(),
  type: z.enum(['main_speaker', 'co_speaker']),
  // Legacy fields for backward compatibility
  name: z.string().optional(),
  company: z.string().optional()
}).refine((data) => {
  // Ensure speaker has either employeeId or userId based on speakerType
  if (data.speakerType === 'employee') {
    return !!data.employeeId;
  } else if (data.speakerType === 'user') {
    return !!data.userId;
  }
  // If speakerType is not set, accept if either ID exists (backward compatibility)
  return !!(data.employeeId || data.userId);
}, {
  message: "Speaker must have either employeeId (for employees) or userId (for users)"
});

// Validation schema for actions
const actionsSchema = z.object({
  type: z.enum(["promotion_technical", "promotion_marketing"]).refine(val => val !== undefined, {
    message: "Aktionstyp ist erforderlich"
  }),
  title: z.array(z.object({
    language: z.string(),
    text: z.string().min(1, "Titel ist erforderlich")
  })).min(1, "Mindestens ein Titel ist erforderlich"),
  content: z.array(z.object({
    language: z.string(),
    text: z.string().min(1, "Beschreibung ist erforderlich")
  })).min(1, "Mindestens eine Beschreibung ist erforderlich"),
  subjects: z.array(z.object({
    de: z.string(),
    en: z.string()
  }))
    .min(3, "Mindestens 3 Unterpunkte erforderlich")
    .max(5, "Maximal 5 Unterpunkte erlaubt")
    .refine(subjects => {
      // At least 3 subjects must be completely filled (both de and en)
      const completelyFilledSubjects = subjects.filter(subject =>
        subject.de && subject.de.trim().length > 0 &&
        subject.en && subject.en.trim().length > 0
      );
      return completelyFilledSubjects.length >= 3;
    }, {
      message: "Mindestens 3 Unterpunkte müssen vollständig ausgefüllt sein (beide Sprachen)"
    })
    .refine(subjects => {
      // If any subject is partially filled, it must be completely filled
      return subjects.every(subject => {
        const hasDeContent = subject.de && subject.de.trim().length > 0;
        const hasEnContent = subject.en && subject.en.trim().length > 0;

        // If neither has content, that's fine (empty subject)
        if (!hasDeContent && !hasEnContent) return true;

        // If either has content, both must have content
        return hasDeContent && hasEnContent;
      });
    }, {
      message: "Wenn ein Unterpunkt ausgefüllt ist, müssen beide Sprachen (Deutsch und Englisch) ausgefüllt sein"
    }),
  conditions: z.array(z.object({
    name: z.string(),
    accepted: z.boolean()
  })).refine(
    conditions => conditions.every(c => c.accepted === true),
    { message: "Alle Bedingungen müssen akzeptiert werden" }
  ),
  lectureMainSpeakerId: z.string().optional(),
  speakers: z.array(speakerSchema).min(1, "Mindestens ein Referent ist erforderlich").optional(),
  image: z.object({
    fileName: z.string(),
    alt: z.string().optional()
  }).optional(),
  // Felder für Standplatz - wichtig für Aktionen
  companyBooth: z.string().min(1, "Standort ist erforderlich"),
  dateTimes: z.array(z.string()).optional()
});

// Configuration for LOUNGES 26 Actions
const lounges26ActionConfig = {
  eventId: "882c49b6-ba4b-4c66-8a35-987d04af08a5", // LOUNGES 26 Event ID
  eventName: "LOUNGES 26",
  validationSchema: actionsSchema,
  availableLectureTypes: [
    "promotion_technical",
    "promotion_marketing"
  ],

  // Entity type labels for actions
  entityType: "action",
  entityTypeLabels: {
    singular: [
      { language: "de", text: "Aktion" },
      { language: "en", text: "Promotion" }
    ],
    plural: [
      { language: "de", text: "Aktionen" },
      { language: "en", text: "Promotions" }
    ]
  },

  // Step content - all text strings adapted for actions
  stepContent: {
    language: {
      heading: [
        { language: "de", text: "Wähle die Sprache der Aktion" },
        { language: "en", text: "Select Promotion Language" }
      ],
      description: [
        { language: "de", text: "Bitte wähle die Sprache, in der deine Aktion stattfindet." },
        { language: "en", text: "Please select the language in which your promotion will be held." }
      ]
    },

    title: {
      heading: [
        { language: "de", text: "Aktionstitel" },
        { language: "en", text: "Promotion Title" }
      ],
      description: [
        { language: "de", text: "Gib einen aussagekräftigen Titel für deine Aktion ein (Minimum 10 Zeichen)." },
        { language: "en", text: "Enter a descriptive title for your promotion (minimum 10 characters)." }
      ]
    },

    content: {
      heading: [
        { language: "de", text: "Aktionsbeschreibung" },
        { language: "en", text: "Promotion Description" }
      ],
      description: [
        { language: "de", text: "Beschreibe deine Aktion detailliert (500-2000 Zeichen)." },
        { language: "en", text: "Describe your promotion in detail (500-2000 characters)." }
      ],
      tipsHeading: [
        { language: "de", text: "Tipps für eine gute Beschreibung:" },
        { language: "en", text: "Tips for a good description:" }
      ],
      tips: [
        { language: "de", text: "Welche Themen werden behandelt?" },
        { language: "en", text: "What topics will be covered?" }
      ],
      tips2: [
        { language: "de", text: "Was werden die Teilnehmer/innen erfahren/lernen?" },
        { language: "en", text: "What will participants learn or discover?" }
      ],
      tips3: [
        { language: "de", text: "An wen richtet sich die Aktion?" },
        { language: "en", text: "Who is the promotion aimed at?" }
      ],
      tips4: [
        { language: "de", text: "Braucht man irgendwelche Voraussetzungen oder Hintergrundwissen?" },
        { language: "en", text: "Are any prerequisites or background knowledge required?" }
      ]
    },

    image: {
      heading: [
        { language: "de", text: "Aktionsbild" },
        { language: "en", text: "Promotion Image" }
      ],
      description: [
        { language: "de", text: "Lade ein Bild für deine Aktion hoch." },
        { language: "en", text: "Upload an image for your promotion." }
      ],
      dragText: [
        { language: "de", text: "Bild hier ablegen oder klicken zum Hochladen" },
        { language: "en", text: "Drop image here or click to upload" }
      ]
    },

    subjects: {
      heading: [
        { language: "de", text: "Themenbereiche" },
        { language: "en", text: "Promotion Subjects" }
      ],
      description: [
        { language: "de", text: "Definiere die Hauptthemen/Inhaltsbereiche deiner Aktion. Du kannst sie durch Ziehen und Ablegen neu anordnen." },
        { language: "en", text: "Please define the main subjects/content areas of your promotion. You can reorder them by dragging and dropping." }
      ]
    },

    conditionsStep: {
      typeNotSelected: [
        { language: "de", text: "Bitte wähle zunächst einen Aktionstyp aus, um die Bedingungen zu sehen." },
        { language: "en", text: "Please select a promotion type first to see applicable conditions." }
      ],
      noConditions: [
        { language: "de", text: "Keine spezifischen Bedingungen erforderlich für diesen Aktionstyp." },
        { language: "en", text: "No specific conditions required for this promotion type." }
      ]
    },

    lecture_type: {
      heading: [
        { language: "de", text: "Aktionstyp" },
        { language: "en", text: "Select Promotion Type" }
      ],
      description: [
        { language: "de", text: "Wähle den Typ für deine Aktion aus." },
        { language: "en", text: "Select the category of your promotion." }
      ],
      noTypes: [
        { language: "de", text: "Keine Aktionstypen für diese Veranstaltung verfügbar." },
        { language: "en", text: "No promotion types available for this event." }
      ],
      typeDescriptionPrefix: [
        { language: "de", text: "Beschreibung:" },
        { language: "en", text: "Description:" }
      ]
    },

    general_conditions: {
      heading: [
        { language: "de", text: "Bedingungen für Aktionen" },
        { language: "en", text: "Promotion Conditions" }
      ],
      description: [
        { language: "de", text: "Bitte lies die folgenden Bedingungen für Aktionen genau durch und akzeptiere diese, um mit der Einreichung fortzufahren." },
        { language: "en", text: "Please read and accept the following conditions for promotions to proceed with your submission." }
      ]
    },

    review: {
      incompleteWarning: [
        { language: "de", text: "Warnung - Aktion unvollständig" },
        { language: "en", text: "Warning - Promotion Incomplete" }
      ],
      incompleteMessage: [
        { language: "de", text: "Deine Aktion ist noch unvollständig und wird in diesem Status nicht zur Programmplanung berücksichtigt." },
        { language: "en", text: "Your promotion is still incomplete and will not be considered for program planning in this state." }
      ],
      completeMissingFields: [
        { language: "de", text: "Bitte fülle die folgenden Felder aus, um deine Aktion zur Programmplanung freizugeben:" },
        { language: "en", text: "Please complete the following points to approve your promotion for program planning:" }
      ],
      saveDraftQuestion: [
        { language: "de", text: "Möchtest du die Aktion trotzdem als Entwurf speichern?" },
        { language: "en", text: "Do you want to save the promotion as a draft anyway?" }
      ],
      submitButton: [
        { language: "de", text: "Aktion einreichen" },
        { language: "en", text: "Submit Promotion" }
      ],
      saveDraftButton: [
        { language: "de", text: "Als Entwurf speichern" },
        { language: "en", text: "Save as Draft" }
      ]
    },

    topics: {
      heading: [
        { language: "de", text: "Themen auswählen" },
        { language: "en", text: "Select Topics" }
      ],
      description: [
        { language: "de", text: "Wähle 1 bis 3 Themen aus, die deine Aktion am besten beschreiben. Klicke auf die Themen, um sie auszuwählen." },
        { language: "en", text: "Select 1 to 3 topics that best describe your promotion. Click on the topics to select them." }
      ]
    },

    booth_location: {
      heading: [
        { language: "de", text: "Standort der Aktion" },
        { language: "en", text: "Promotion Location" }
      ],
      description: [
        { language: "de", text: "Deine Aktion findet an einem deiner gebuchten Stände statt. Bitte wähle aus, an welchem Stand die Aktion stattfinden soll." },
        { language: "en", text: "Your promotion takes place at one of your booked booths. Please select at which booth the promotion will take place." }
      ],
      singleBoothInfo: [
        { language: "de", text: "Diese Aktion wird an diesem Stand stattfinden." },
        { language: "en", text: "This promotion will take place at this booth." }
      ],
      multipleBoothsPrompt: [
        { language: "de", text: "Du hast mehrere Stände gebucht. Bitte wähle aus, an welchem Stand diese Aktion stattfinden soll." },
        { language: "en", text: "You have booked multiple booths. Please select at which booth this promotion should take place." }
      ],
      noBoothsWarning: [
        { language: "de", text: "Es wurden keine gebuchten Stände für dieses Event gefunden. Bitte wende dich an den Support." },
        { language: "en", text: "No booked booths were found for this event. Please contact support." }
      ]
    }
  },

  // Review step section labels
  reviewSections: {
    general: {
      title: [
        { language: "de", text: "Allgemeine Angaben" },
        { language: "en", text: "General Information" }
      ]
    },
    details: {
      title: [
        { language: "de", text: "Aktionsdetails" },
        { language: "en", text: "Promotion Details" }
      ]
    }
  },

  // Review step field labels
  reviewLabels: {
    general_conditions: [
      { language: "de", text: "Aktionsbedingungen" },
      { language: "en", text: "Promotion Conditions" }
    ],
    lecture_type: [
      { language: "de", text: "Aktionstyp" },
      { language: "en", text: "Promotion Type" }
    ],
    language: [
      { language: "de", text: "Sprache der Aktion" },
      { language: "en", text: "Promotion Language" }
    ],
    title: [
      { language: "de", text: "Titel" },
      { language: "en", text: "Title" }
    ],
    subjects: [
      { language: "de", text: "Unterpunkte" },
      { language: "en", text: "Subjects" }
    ],
    description: [
      { language: "de", text: "Beschreibung" },
      { language: "en", text: "Description" }
    ],
    topics: [
      { language: "de", text: "Themenbereich" },
      { language: "en", text: "Topic Area" }
    ],
    speakers: [
      { language: "de", text: "Referenten" },
      { language: "en", text: "Speakers" }
    ],
    image: [
      { language: "de", text: "Bild" },
      { language: "en", text: "Image" }
    ],
    handout: [
      { language: "de", text: "Handout" },
      { language: "en", text: "Handout" }
    ],
    presentation: [
      { language: "de", text: "Präsentation" },
      { language: "en", text: "Presentation" }
    ],
    booth_location: [
      { language: "de", text: "Standort" },
      { language: "en", text: "Location" }
    ]
  },

  // Steps configuration - EXAKT wie Lectures aber ohne Upload Steps
  // Diese wird nicht direkt verwendet, sondern über stepsConfig function

  // Info texts for actions
  infoTexts: {
    de: {
      title: "Informationen",
      content: `
        <h3>Einreichung von Aktionen für die LOUNGES 2027</h3>
        <p>Bevor Du mit der Einreichung beginnst, lese die nachfolgenden Informationen bitte sorgfältig durch.</p>
        <p><strong>Nur wenn die genannten Termine und Standards eingehalten werden, kann die Aktion angenommen werden.</strong></p>

        <h4>Wichtige Termine</h4>
        <ul>
          <li>Beginn der Einreichung: 15. August 2026</li>
          <li>Einreichungsschluss: 30. November 2026</li>
          <li>Bestätigungsfrist eingereichter/unvollständiger Aktionen: 30. November 2026</li>
          <li>Keine Einreichungen mehr ab: 1. Dezember 2026</li>
        </ul>

        <h4>Arten von Aktionen</h4>
        <p>Folgende zwei Kategorien stehen zur Auswahl.</p>

        <h5>Aktion Fachthema</h5>
        <ul>
          <li>Expertenwissen, Forschungsergebnisse, Guidelines, Innovationen, Kundenprojekte</li>
          <li>Aktion wird auf der Internetseite sowie über das Programm beworben</li>
          <li>Am Ausstellungsstand oder an einer gebuchten Zusatzfläche</li>
        </ul>

        <h5>Aktion Marketingvortrag</h5>
        <ul>
          <li>Wert und Nutzen von Produkten, Dienstleistungen, Ideen. Präsentation der Marke.</li>
          <li>Aktion wird auf der Internetseite sowie über das Programm beworben</li>
          <li>Am Ausstellungsstand oder an einer gebuchten Zusatzfläche</li>
        </ul>

        <p><strong>Bitte beachten:</strong> Eine Aufzeichnung der Aktionen erfolgt nicht.</p>

        <h4>Qualitätsstandards</h4>
        <ul>
          <li>Referenten sind Experten ihres Fachgebiets</li>
          <li>Aktionen müssen qualitativ hochwertig und didaktisch wertvoll sein</li>
          <li>Aktionen zu Fachthemen sind werbefrei</li>
          <li>Aussagekräftige Titel, informative Unterpunkte, mindestens 500 Zeichen Inhaltsbeschreibung und ein passendes Bild zum Thema der Aktion erforderlich</li>
        </ul>
      `
    },
    en: {
      title: "Information",
      content: `
        <h3>Submission of Promotions for LOUNGES 2027</h3>
        <p>Before you begin the submission, please read the following information carefully.</p>
        <p><strong>Only if the mentioned deadlines and standards are met can the presentation be accepted.</strong></p>

        <h4>Important Dates</h4>
        <ul>
          <li>Start of submission: November 1, 2025</li>
          <li>Submission deadline: November 30, 2025</li>
          <li>Confirmation deadline for submitted/incomplete promotions: November 30, 2025</li>
          <li>No more submissions from: December 1, 2025</li>
        </ul>

        <h4>Types of Promotions</h4>
        <p>The following two categories are available.</p>

        <h5>Technical Topic Promotion</h5>
        <ul>
          <li>Expert knowledge, research results, guidelines, innovations, customer projects</li>
          <li>Promotion will be advertised on the website and through the program</li>
          <li>At the exhibition stand or at a booked additional area</li>
        </ul>

        <h5>Marketing Presentation Promotion</h5>
        <ul>
          <li>Value and benefits of products, services, ideas. Brand presentation. For potential customers, business partners.</li>
          <li>Promotion will be advertised on the website and through the program</li>
          <li>At the exhibition stand or at a booked additional area</li>
        </ul>

        <p><strong>Please note:</strong> Promotions will not be recorded.</p>

        <h4>Quality Standards</h4>
        <ul>
          <li>Speakers are experts in their field</li>
          <li>Promotions must be of high quality and didactically valuable</li>
          <li>Promotions on technical topics are advertising-free</li>
          <li>Meaningful title, informative sub-points, at least 500 characters content description and a suitable image on the topic of the presentation required</li>
        </ul>
      `
    }
  },

  // Type descriptions
  typeDescriptions: {
    promotion_technical: {
      de: {
        title: "Aktionen zu Fachthemen – mach neugierig durch Fachkompetenz",
        description: "Wissen zu Guidelines, aktuellen Forschungsergebnissen, Projektberichten oder branchenspezifischen Neuerungen.\n\nTags: Expertenwissen | Forschung | Projekte | Richtlinien | Werbefrei"
      },
      en: {
        title: "Technical Presentation – Spark Curiosity Through Expertise",
        description: "Knowledge about guidelines, current research results, project reports or industry-specific innovations.\n\nTags: Expert Knowledge | Research | Projects | Guidelines | Advertising-free"
      }
    },
    promotion_marketing: {
      de: {
        title: "Aktionen zu Marketing – erreiche Kunden",
        description: "Werbe für Produkte oder Dienstleistungen\n\nTags: Branding | Marketing | Kontakte | Leads"
      },
      en: {
        title: "Marketing Presentation – Reach Customers",
        description: "Advertise products or services\n\nTags: Branding | Marketing | Contacts | Leads"
      }
    }
  },

  // Information content for general conditions header
  generalConditionsContent: [
    {
      language: "de",
      text: "Bitte lies die folgenden allgemeinen Bedingungen genau durch und akzeptiere diese, um mit der Einreichung der Aktionen fortzufahren zu können. Das Akzeptieren aller Bedingungen ist erforderlich."
    },
    {
      language: "en",
      text: "Please read the following general terms and conditions carefully and accept them to proceed with the submission of actions. Accepting all conditions is required."
    }
  ],

  // General conditions for actions - mit den korrekten Texten
  generalConditions: [
    {
      id: "submission_deadlines",
      title: [
        { language: "de", text: "Einreichungsfristen" },
        { language: "en", text: "Submission Deadlines" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich habe die Einreichungsfristen gelesen und akzeptiert." },
        { language: "en", text: "I have read and accept the submission deadlines." }
      ],
      content: [
        { language: "de", text: "Aktionen können ab dem 15. August 2026 eingereicht und müssen vollständig bis 30. November 2026 bestätigt werden. Nach dem 1. Dezember 2026 können keine Aktionen mehr eingereicht werden. Unvollständige Aktionen nach dem 30. November 2026 werden nicht ins Programm aufgenommen." },
        { language: "en", text: "Actions can be submitted from November 1, 2025 and must be fully confirmed by November 30, 2025. After December 1, 2025, no more actions can be submitted. Incomplete actions after November 30, 2025 will not be included in the program." }
      ],
      required: true
    },
    {
      id: "advertising_free_content",
      title: [
        { language: "de", text: "Werbefreiheit der Aktionen" },
        { language: "en", text: "Advertising-Free Actions" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich habe verstanden, dass die Aktion werbefrei sein muss." },
        { language: "en", text: "I understand that the presentation must be advertising-free and accept this." }
      ],
      content: [
        { language: "de", text: "Aktionen zu Fachthemen sind werbefrei (keine Produktnamen, Datenblätter oder Firmeninformationen)." },
        { language: "en", text: "Technical actions must be advertising-free (no product names, data sheets or company information)." }
      ],
      required: true
    },
    {
      id: "speaker_expertise",
      title: [
        { language: "de", text: "Qualifikation der Referenten" },
        { language: "en", text: "Speaker Qualification" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich bestätige, dass die Referenten qualifiziert sind." },
        { language: "en", text: "I confirm that the speakers are qualified." }
      ],
      content: [
        { language: "de", text: "Referenten sind Experten ihres Themas und bieten qualitativ hochwertige und didaktisch wertvolle Aktionen." },
        { language: "en", text: "Speakers are experts in their field and provide high-quality and didactically valuable actions." }
      ],
      required: true
    },
    {
      id: "program_scheduling",
      title: [
        { language: "de", text: "Programmplanung" },
        { language: "en", text: "Program Planning" }
      ],
      checkboxLabel: [
        { language: "de", text: "Ich bin mit der Programmgestaltung / Zeiteinteilung durch den Veranstalter einverstanden." },
        { language: "en", text: "I agree with the program design / time scheduling by the organizer." }
      ],
      content: [
        { language: "de", text: "Die Programmzuordnung erfolgt durch den Veranstalter und wird dem Admin und Referent bis zum 31. Januar 2027 mitgeteilt." },
        { language: "en", text: "The program assignment is done by the organizer and will be communicated to the admin and speaker by January 31, 2026." }
      ],
      required: true
    }
  ],

  // Conditions for actions
  conditions: [
    {
      name: "script",
      de: {
        label: "Ablaufplan",
        text: "Ich stelle einen detaillierten Ablaufplan für meine Aktion zur Verfügung."
      },
      en: {
        label: "Script",
        text: "I will provide a detailed script for my promotion."
      }
    },
    {
      name: "image",
      de: {
        label: "Bildmaterial",
        text: "Ich stelle Bildmaterial für die Bewerbung meiner Aktion zur Verfügung."
      },
      en: {
        label: "Image Material",
        text: "I will provide image material for promoting my action."
      }
    },
    {
      name: "technical",
      de: {
        label: "Technische Anforderungen",
        text: "Ich habe die technischen Anforderungen mit dem Organisationsteam abgeklärt."
      },
      en: {
        label: "Technical Requirements",
        text: "I have clarified the technical requirements with the organizing team."
      }
    },
    {
      name: "timing",
      de: {
        label: "Zeitrahmen",
        text: "Ich halte mich an den vereinbarten Zeitrahmen von max. 30 Minuten."
      },
      en: {
        label: "Time Frame",
        text: "I will adhere to the agreed time frame of max. 30 minutes."
      }
    }
  ],

  // Steps configuration function - EXAKT wie bei Lectures, aber ohne Upload-Substeps
  stepsConfig: function(t) {
    return [
      {
        id: "information",
        title: t("Information"),
        component: "InformationStep",
        expandable: false,
        expanded: false,
        complete: false,
        active: false,
        neutralDisplay: false,
        isLast: false
      },
      {
        id: "general_conditions",
        title: t("Action Conditions"), // Angepasst für Aktionen
        component: "GeneralConditionsStep",
        expandable: false,
        expanded: false,
        complete: false,
        active: false,
        neutralDisplay: false, // Changed to false to show completion status
        isLast: false
      },
      {
        id: "lecture_type",
        title: t("Action Type"), // Angepasst für Aktionen
        component: "LectureTypeStep",
        expandable: false,
        expanded: false,
        complete: false,
        active: false,
        neutralDisplay: false,
        isLast: false
      },
      {
        id: "details",
        title: t("Action Details"), // Angepasst für Aktionen
        component: null,
        expandable: true,
        expanded: true,
        complete: false,
        active: false,
        substeps: [
          {
            id: "language",
            title: t("Language"),
            component: "LanguageStep",
            complete: false,
            active: false,
            neutralDisplay: false
          },
          {
            id: "title",
            title: t("Title"),
            component: "TitleStep",
            complete: false,
            active: false,
            neutralDisplay: false,
            required: true
          },
          {
            id: "subjects",
            title: t("Subjects"),
            component: "SubjectsStep",
            complete: false,
            active: false,
            neutralDisplay: false
          },
          {
            id: "description",
            title: t("Description"),
            component: "DescriptionStep",
            complete: false,
            active: false,
            neutralDisplay: false
          },
          {
            id: "topics",
            title: t("Topics"),
            component: "TopicsStep",
            complete: false,
            active: false,
            neutralDisplay: false
          },
          {
            id: "speakers",
            title: t("Speakers"),
            component: "SpeakersStep",
            complete: false,
            active: false,
            neutralDisplay: false
          },
          {
            id: "image",
            title: t("Image"),
            component: "ImageStep",
            complete: false,
            active: false,
            neutralDisplay: false
          }
        ]
      },
       {
            id: "booth_location",
            title: t("Location"),
            component: "BoothLocationStep",
            complete: false,
            active: false,
            neutralDisplay: false,
            required: true
          },
      {
        id: "review",
        title: t("Review"),
        component: "ReviewStep",
        expandable: false,
        expanded: false,
        complete: false,
        active: false,
        neutralDisplay: true,
        isLast: true
      }
    ];
  },

  // Helper functions
  getAllRequiredFields: function() {
    // Definiere die erforderlichen Felder für Actions
    return [
      "type",
      "generalConditions",
      "presentationLanguage",
      "title",
      "content",
      "subjects",
      "speakers",
      "companyBooth"
    ];
  },

  // Required fields for validation
  requiredFields: [
    "type",
    "generalConditions",
    "presentationLanguage",
    "title",
    "content",
    "subjects",
    "speakers",
    "companyBooth"
  ],

  validateFormData: function(formData) {
    try {
      // Validiere mit dem Actions-Schema
      actionsSchema.parse(formData);
      return { isValid: true, errors: [] };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return {
          isValid: false,
          errors: error.errors.map(err => ({
            path: err.path.join('.'),
            message: err.message
          }))
        };
      }
      captureError(error, {
        feature: 'action-validation',
        operation: 'validate-form-data',
        consoleMessage: 'Unknown validation error',
        context: { formData }
      });
      return {
        isValid: false,
        errors: [{ path: '', message: 'Unbekannter Validierungsfehler' }]
      };
    }
  }
};

export default lounges26ActionConfig;